import React, { useMemo } from "react";
import { useGet, usePost } from "../../../../../api/useCall";
import t from "../../../../../i18n";
import ModalForm from "@ui/Modal/Form";
import type { Project } from "../../../../../api/useProjects";
import { isResultErr } from "../../../../../lib/patterns/result";
import DropzoneV2 from "@ui/DropzoneV2";
import type {
  QuantityParsingError,
  QuantityParsingResult,
} from "../../../../../domain/parser/quantity/csv";
import { parseQuantity } from "../../../../../domain/parser/quantity/csv";
import AdditionnalMessage from "@ui/AdditionnalMessage";
import { getQuantityParsingErrorMessage } from "../../../../../domain/parser/quantity/csv/errors";
import Details from "@ui/Details";
import { css } from "@linaria/core";
import Text from "@ui/Text";
import { styled } from "@linaria/react";
import { color } from "@ui";
import type {
  DryRunImportQuantitiesImpact,
  FormulaVersionDryRunImpactGroup,
  PartialDryRunImportQuantitiesImpact,
  PostQuantities,
  QuantityDryRunError,
} from "../utils/ParseQuantitiesDryRun";
import {
  getFormulaVersionDryRunHeaderMessage,
  getFormulaVersionDryRunInfoMessage,
  getParseDryRun,
  getQuantityDryRunErrorMessage,
  getQuantityDryRunInfoMessage,
} from "../utils/ParseQuantitiesDryRun";
import { notNull } from "diagnostics-lib/typescript";
import { MAX_QUANTITY_COUNT } from "diagnostics-domain/constants";
import { uniq } from "diagnostics-lib/array";
const BlockMessage = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const DetailsCustom = styled(Details)`
  margin-left: 20px;
`;

const SummaryCustom = styled(Text)`
  color: ${color.carbon800};
`;

const SublistItem = styled.li`
  margin-top: 0.5em;
  margin-left: 20px;
  position: relative;
  display: flex;
  color: ${color.carbon800};
`;

const NestedList = styled.ul`
  margin-left: 16px;
`;

const Puce = styled.div`
  margin-top: 0.5em;
  margin-left: 0.5em;
  flex-shrink: 0;
  height: 3px;
  width: 3px;
  border-radius: 50%;
  background-color: ${color.carbon600};
`;
const TextCustom = styled(Text)`
  padding-left: 0.5em;
  color: ${color.carbon800};
`;

const AdditionnalMessageBlock = css`
  & + & {
    margin-top: 8px;
  }
`;

interface ImportQuantitiesProps {
  onRequestClose: () => void;
  project: Project;
  onSetEditedDataKey: (key?: string) => void;
}

const additionnalMessageInfoImpactType: DryRunImportQuantitiesImpact[] = [
  "DELETED_METROLOGY_CROSS_FILTER",
  "DELETED_QOI",
  "DELETED_PROCESS_INDICATOR",
  "DELETED_ACTIVATION_FILTER",
  "FORMULA_OR_METROLOGY_UPDATED",
  "FORMULA_OR_METROLOGY_UPDATED_IN_SETUPS",
];

const ImportQuantitiesModal = ({
  onRequestClose,
  project,
  onSetEditedDataKey,
}: ImportQuantitiesProps) => {
  /** Import */
  const sensors = useGet("/projects/:projectId(uuid)/sensors");
  const postQuantities = usePost("/projects/:projectId(uuid)/quantities/import");

  /** ------- Dropzone states ------- */
  /** File state */
  const [fileName, setFilename] = React.useState("");
  /** Content state */
  const [isContentValid, setContentValid] = React.useState<boolean>();
  const [contentResult, setContentResult] = React.useState<QuantityParsingResult>();
  const [errorText, setErrorText] = React.useState<string>();
  const [errorDetails, setErrorDetails] = React.useState<QuantityParsingError[]>();
  /* Dry-run state */
  const [dryRunResponse, setDryRunResponse] = React.useState<PostQuantities | null>(null);
  const [isDryRunValid, setDryRunValid] = React.useState<boolean>();
  const [errorTextDryRun, setErrorTextDryRun] = React.useState<string>();
  const [errorDetailsDryRun, setErrorDetailsDryRun] = React.useState<QuantityDryRunError[]>();
  const [quantityNameByImpactInfo, setQuantityNameByImpactInfo] =
    React.useState<Record<string, string[]>>();
  const [formulaVersionImpacts, setFormulaVersionImpacts] = React.useState<
    FormulaVersionDryRunImpactGroup[]
  >([]);
  const [categoryCreationImpacts, setCreationCategoryInfos] = React.useState<{
    systems: string[];
    equipmentTypes: string[];
    equipments: string[];
  }>({ systems: [], equipmentTypes: [], equipments: [] });

  const impactInfosWithQuantities = useMemo(() => {
    if (!isDryRunValid || !quantityNameByImpactInfo) return [];

    const impactInfosWithQuantities: {
      info: DryRunImportQuantitiesImpact;
      quantitiesLinked: string[];
    }[] = additionnalMessageInfoImpactType
      .map(info => {
        const quantitiesLinked = quantityNameByImpactInfo[info];
        return quantitiesLinked && quantitiesLinked.length > 0 ? { info, quantitiesLinked } : null;
      })
      .filter(notNull);
    const hasBothInfoTypes = [
      "FORMULA_OR_METROLOGY_UPDATED",
      "FORMULA_OR_METROLOGY_UPDATED_IN_SETUPS",
    ].every(type => impactInfosWithQuantities.some(info => info.info === type));
    if (hasBothInfoTypes) {
      const indexToRemove = impactInfosWithQuantities.findIndex(
        f => f.info === "FORMULA_OR_METROLOGY_UPDATED",
      );
      if (indexToRemove !== -1) {
        impactInfosWithQuantities.splice(indexToRemove, 1);
      }
    }
    return impactInfosWithQuantities;
  }, [isDryRunValid, quantityNameByImpactInfo]);

  const handleDrop = React.useCallback(
    async ([file]: File[]) => {
      setContentValid(undefined);
      setErrorText(undefined);
      setErrorDetails(undefined);

      setDryRunValid(undefined);
      setDryRunResponse(null);
      setErrorTextDryRun(undefined);
      setErrorDetailsDryRun(undefined);
      setQuantityNameByImpactInfo(undefined);
      setFormulaVersionImpacts([]);

      if (!file) return;

      const sensorsResponse = await sensors({
        routeParams: { projectId: project.id },
      });
      const sensorTags = sensorsResponse.body.map(sensor => sensor.tag);

      /**
       * Keep track of the file name to display inside the dropzone.
       */
      setFilename(file.name);

      /**
       * Parse file according to the expected format and content rules.
       */
      const result = await parseQuantity(file, {
        sensorTags,
      });

      /**
       * Catch for parsing errors.
       * Display humain readable error message and allow for downloading the error details.
       */
      if (isResultErr(result)) {
        setContentValid(false);
        if (result.value[0]) setErrorText(getQuantityParsingErrorMessage(result.value[0]));
        setErrorDetails(result.value);
        return;
      }

      const quantities = result.value.data.quantities;
      const formulaVersions = result.value.data.formulaVersions;

      // too many quantities
      if (result.value.data.quantities.length > MAX_QUANTITY_COUNT) {
        setContentValid(false);
        setErrorText(t("projects.quantities.too_many"));
        setErrorDetails(undefined);

        return;
      }

      /**
       * Parsing was successful.
       * Save the result in the state to be used in the submit handler.
       */
      setContentValid(true);
      setContentResult(result.value);

      if (quantities.length > 0) {
        // Check the content validity with a dry-run
        const {
          body: { isValid, impactedQuantities },
        } = await postQuantities({
          body: { quantities, formulaVersions, fileName },
          query: { "dry-run": true },
          routeParams: { projectId: project.id },
        });

        setDryRunValid(isValid);
        setDryRunResponse(impactedQuantities);

        const { errors, quantityNameByImpactInfo, categoryCreationImpacts, formulaVersionImpacts } =
          getParseDryRun(impactedQuantities);

        if (errors[0]) {
          setErrorTextDryRun(getQuantityDryRunErrorMessage(errors[0]));
          setErrorDetailsDryRun(errors);
        } else {
          setQuantityNameByImpactInfo(quantityNameByImpactInfo);
          setCreationCategoryInfos(categoryCreationImpacts);
          setFormulaVersionImpacts(formulaVersionImpacts);
        }
      }
    },
    [fileName, postQuantities, project.id, sensors],
  );

  const handleSubmit = React.useCallback(async () => {
    if (!contentResult) throw new Error("No content result");

    await postQuantities({
      body: {
        quantities: contentResult.data.quantities,
        formulaVersions: contentResult.data.formulaVersions,
        fileName,
      },
      query: { "dry-run": false },
      routeParams: { projectId: project.id },
    });
    onSetEditedDataKey();
    onRequestClose();
    return true;
  }, [contentResult, onRequestClose, onSetEditedDataKey, postQuantities, project.id, fileName]);

  const downloadErrorDetails = React.useCallback(() => {
    const errors = JSON.stringify(errorDetails ? errorDetails : errorDetailsDryRun, null, 4);
    const a = document.createElement("a");
    a.setAttribute("href", URL.createObjectURL(new Blob([errors], { type: "application/json" })));
    a.setAttribute("download", `${new Date().toISOString()}_quantity_import_errors.json`);
    a.click();
  }, [errorDetails, errorDetailsDryRun]);

  const isValid = isContentValid && isDryRunValid;
  const errorTextAll = isContentValid ? (isDryRunValid ? undefined : errorTextDryRun) : errorText;

  return (
    <ModalForm
      onRequestClose={onRequestClose}
      onSubmit={
        isContentValid &&
        contentResult &&
        contentResult.data.quantities.length &&
        dryRunResponse &&
        isDryRunValid
          ? handleSubmit
          : false
      }
      okButtonName={t("modalForm.button.name.import")}
    >
      <DropzoneV2
        onDrop={handleDrop}
        accept=".csv"
        nameFile={fileName}
        isLoading={postQuantities.loading}
        validation={isValid}
        pathSampleFile="/metroscopia_example_quantities.csv"
        multiple={false}
        errorText={errorTextAll}
        downloadErrors={(errorDetails ?? errorDetailsDryRun) ? downloadErrorDetails : undefined}
      />
      {impactInfosWithQuantities.map(q => (
        <AdditionnalMessage
          key={q.info}
          status="WARNING"
          text={getQuantityDryRunInfoMessage(
            q.info as PartialDryRunImportQuantitiesImpact,
            q.quantitiesLinked,
          )}
        />
      ))}
      {formulaVersionImpacts.length > 0 && (
        <BlockMessage>
          <AdditionnalMessage
            status="INFO"
            text={t("quantities.import.dry_run.message.formula_versions_info")}
          />
          <DetailsCustom
            className={AdditionnalMessageBlock}
            summary={
              <SummaryCustom type="body2">
                {t("quantities.import.dry_run.message.formula_versions_summary")}
              </SummaryCustom>
            }
            text={
              <ul>
                {formulaVersionImpacts.map(impact => (
                  <li key={impact.quantityName}>
                    <SublistItem>
                      <Puce />
                      <TextCustom type="body2">
                        {getFormulaVersionDryRunHeaderMessage(impact.quantityName)}
                      </TextCustom>
                    </SublistItem>
                    <NestedList>
                      {impact.versions.map(version => (
                        <SublistItem
                          key={`${impact.quantityName}-${version.startsAt}-${version.label}`}
                        >
                          <Puce />
                          <TextCustom type="body2">
                            {getFormulaVersionDryRunInfoMessage(version)}
                          </TextCustom>
                        </SublistItem>
                      ))}
                    </NestedList>
                  </li>
                ))}
              </ul>
            }
          />
        </BlockMessage>
      )}
      {isValid &&
      (categoryCreationImpacts.systems.length > 0 ||
        categoryCreationImpacts.equipments.length > 0 ||
        categoryCreationImpacts.equipmentTypes.length > 0) ? (
        <BlockMessage>
          <AdditionnalMessage
            status="INFO"
            text={t("setup.import.quantities.message.info.to_be_created")}
          />
          <DetailsCustom
            className={AdditionnalMessageBlock}
            summary={
              <SummaryCustom type="body2">
                {t("setup.import.quantities.message.info.to_be_created.details.summary")}
              </SummaryCustom>
            }
            text={
              <CreateCategoryDetails
                systems={categoryCreationImpacts.systems}
                equipmentTypes={categoryCreationImpacts.equipmentTypes}
                equipments={categoryCreationImpacts.equipments}
              />
            }
          />
        </BlockMessage>
      ) : null}
      {isValid && contentResult?.data.ignoredQuantities.length ? (
        <BlockMessage>
          <AdditionnalMessage
            status="WARNING"
            text={t("setup.import.quantities.message.warning.ignore_sensorTag")}
          />
          <DetailsCustom
            className={AdditionnalMessageBlock}
            summary={
              <SummaryCustom type="body2">
                {t("setup.import.quantities.message.info.ignore_sensor_tags.details.summary")}
              </SummaryCustom>
            }
            text={
              <>
                <IgnoredQuantityDetails ignores={contentResult?.data.ignoredQuantities} />
                <IgnoredQuantityDetails ignores={contentResult?.data.ignoredFormulas} />
              </>
            }
          />
        </BlockMessage>
      ) : null}
    </ModalForm>
  );
};

export default ImportQuantitiesModal;

function CreateCategoryDetails({
  systems,
  equipmentTypes,
  equipments,
}: {
  systems: string[];
  equipmentTypes: string[];
  equipments: string[];
}) {
  const systemsSorted = uniq(systems).sort((a, b) => a.localeCompare(b));
  const equipmentTypesSorted = uniq(equipmentTypes).sort((a, b) => a.localeCompare(b));
  const equipmentsSorted = uniq(equipments).sort((a, b) => a.localeCompare(b));
  return (
    <ul>
      {systems.length > 0 && (
        <SublistItem>
          <Puce />
          <TextCustom type="body2">
            {t("setup.import.quantities.message.info.to_be_created.details.system", systemsSorted)}
          </TextCustom>
        </SublistItem>
      )}
      {equipmentTypesSorted.length > 0 && (
        <SublistItem>
          <Puce />
          <TextCustom type="body2">
            {t(
              "setup.import.quantities.message.info.to_be_created.details.equipment_type",
              equipmentTypesSorted,
            )}
          </TextCustom>
        </SublistItem>
      )}
      {equipmentsSorted.length > 0 && (
        <SublistItem>
          <Puce />
          <TextCustom type="body2">
            {t(
              "setup.import.quantities.message.info.to_be_created.details.equipment",
              equipmentsSorted,
            )}
          </TextCustom>
        </SublistItem>
      )}
    </ul>
  );
}
function IgnoredQuantityDetails({
  ignores,
}: {
  ignores: { quantity: string; sensorTag?: string }[];
}) {
  return (
    <ul>
      {ignores.map(elem => (
        <SublistItem key={elem.quantity + " " + elem.sensorTag}>
          <Puce />
          <TextCustom type="body2">
            {t(
              "setup.import.quantities.message.info.ignore_sensor_tags.details",
              elem.quantity,
              elem.sensorTag || "",
            )}
          </TextCustom>
        </SublistItem>
      ))}
    </ul>
  );
}
